import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Bitcoin, DollarSign } from 'lucide-vue-next'
import { useCurrencyStore } from '@/stores/currencyStore'
import type { SupportedCurrency, CurrencyOption } from '@/types/currency'

/**
 * Composable for currency operations with route integration
 * Provides centralized currency state management and formatting utilities
 */
export function useCurrency() {
  const currencyStore = useCurrencyStore()
  const router = useRouter()
  const route = useRoute()

  // Currency options for selectors
  const currencyOptions = computed<CurrencyOption[]>(() => [
    { value: 'BTC', label: 'BTC', icon: Bitcoin },
    { value: 'LTC', label: 'LTC', icon: DollarSign }
  ])

  // Sync with route - watch for route changes
  watch(
    () => route?.query?.coin,
    (newCoin) => {
      if (newCoin && typeof newCoin === 'string') {
        const currency = newCoin.toUpperCase() as SupportedCurrency
        if (currency !== currencyStore.selectedCurrency) {
          currencyStore.initializeFromRoute(currency)
        }
      }
    },
    { immediate: false } // Don't run immediately since router.beforeEach handles initial sync
  )

  
  function updateRouteWithCurrency(currency: SupportedCurrency) {
    try {
      if (!route) return
      const newQuery = { ...route.query, coin: currency }
      router.replace({ ...route, query: newQuery })
    } catch (error) {
      console.warn('Failed to update route with currency:', error)
    }
  }

  // Currency formatting functions
  function formatCurrencyAmount(amount: string | number, showSymbol = true): string {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    const formattedAmount = numericAmount.toFixed(8)
    return showSymbol ? `${formattedAmount} ${currencyStore.selectedCurrency}` : formattedAmount
  }

  function formatCurrencyDisplay(amount: string | number, fallback = '0.00000000'): string {
    if (!amount || amount === 0) {
      return `${fallback} ${currencyStore.selectedCurrency}`
    }
    return formatCurrencyAmount(amount)
  }

  function formatShortCurrency(amount: string | number): string {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (numericAmount >= 1) {
      return `${numericAmount.toFixed(4)} ${currencyStore.selectedCurrency}`
    }
    return formatCurrencyAmount(amount)
  }

  // Currency validation
  function isValidCurrency(currency: string): currency is SupportedCurrency {
    return currencyStore.supportedCurrencies.includes(currency as SupportedCurrency)
  }

  // Get currency-specific configurations
  function getCurrencyConfig(currency?: SupportedCurrency) {
    const curr = currency || currencyStore.selectedCurrency
    
    const configs = {
      BTC: {
        symbol: 'BTC',
        name: 'Bitcoin',
        decimals: 8,
        icon: Bitcoin,
        defaultAmount: '0.00000000'
      },
      LTC: {
        symbol: 'LTC', 
        name: 'Litecoin',
        decimals: 8,
        icon: DollarSign,
        defaultAmount: '0.00000000'
      }
    }

    return configs[curr]
  }

  // Initialize currency from various sources
  function initializeCurrency() {
    // First try route
    const routeCoin = route?.query?.coin as string
    if (routeCoin) {
      currencyStore.initializeFromRoute(routeCoin)
    } else {
      // Then try localStorage
      currencyStore.initializeFromStorage()
    }
  }

  
  function setCurrency(currency: SupportedCurrency) {
    currencyStore.setCurrency(currency)
    // Route update is handled by the watcher above, no need to call updateRouteWithCurrency here
  }

  return {
    // State
    selectedCurrency: currencyStore.selectedCurrency,
    currencySymbol: currencyStore.currencySymbol,
    isMultiCurrencyEnabled: currencyStore.isMultiCurrencyEnabled,
    supportedCurrencies: currencyStore.supportedCurrencies,
    
    // Options and configs
    currencyOptions,
    getCurrencyConfig,
    
    // Actions
    setCurrency,
    initializeCurrency,
    
    // Formatting utilities
    formatCurrencyAmount,
    formatCurrencyDisplay,
    formatShortCurrency,
    
    // Validation
    isValidCurrency,
    
    // Route integration
    updateRouteWithCurrency
  }
} 